import {useEffect, useEffectEvent, useState} from 'react'

/**
 * Detects whether any attachment label is visually truncated (its text overflows
 * the token's fixed max-width). Mirrors copilot-chat's overflow detection so the
 * composer can offer a "Show full attachment names" toggle only when it's needed.
 *
 * A single ResizeObserver watches the container — when tokens are added,
 * removed, or the container resizes, the observer fires and we re-measure.
 * Observing only the container (not each label) keeps the flag sticky while the
 * user has expanded the labels, so the toggle to collapse them again stays
 * available. Layout reads are deferred to rAF so the ResizeObserver callback
 * stays off the critical path.
 */
export function useAttachmentOverflow(
  count: number,
  labelElementsRef: React.RefObject<Array<HTMLElement | null>>,
  containerRef: React.RefObject<HTMLElement | null>,
): boolean {
  const [hasOverflowingText, setHasOverflowingText] = useState(false)

  const checkForOverflow = useEffectEvent(() => {
    const labels = labelElementsRef.current
    if (!labels) {
      setHasOverflowingText(false)
      return
    }

    // Batch the layout reads and short-circuit on the first truncated label.
    const hasOverflow = labels.some(el => el != null && el.scrollWidth > el.clientWidth)
    setHasOverflowingText(hasOverflow)
  })

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    let frameId: number | undefined
    const scheduleCheck = () => {
      if (frameId !== undefined) cancelAnimationFrame(frameId)
      frameId = requestAnimationFrame(checkForOverflow)
    }

    const resizeObserver = new ResizeObserver(scheduleCheck)
    resizeObserver.observe(container)

    // Also run once immediately for the initial state.
    scheduleCheck()

    return () => {
      if (frameId !== undefined) cancelAnimationFrame(frameId)
      resizeObserver.disconnect()
    }
  }, [containerRef])

  // Overflow only matters while there are attachments to reveal.
  if (hasOverflowingText && count === 0) {
    setHasOverflowingText(false)
  }

  return hasOverflowingText && count > 0
}
